/**
 * AssignmentPlayerPage — agent-facing viewer for one learning assignment.
 * Left rail: item list with progress. Main: active item viewer. Quiz gate at
 * the end (if attached). Sends a 30s time heartbeat while open.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Presentation, FileText, Video, Youtube, Link2, Package,
  CheckCircle2, Circle, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { ModuleViewer, PdfViewer, VideoViewer, YouTubeViewer, LinkViewer } from './ItemViewers';
import { ScormViewer } from './ScormViewer';
import QASection from './QASection';

const TYPE_ICON = { module: Presentation, document: FileText, video: Video, youtube: Youtube, link: Link2, scorm: Package };

export default function AssignmentPlayerPage() {
  const { assignmentId } = useParams();
  const { get, post } = useApi();
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const activeRef = useRef(null);
  activeRef.current = activeId;

  const load = useCallback(async () => {
    const res = await get(`/me/learning/${assignmentId}`);
    setData(res);
    return res;
  }, [get, assignmentId]);

  useEffect(() => {
    load().then(res => {
      const firstIncomplete = (res.items || []).find(i => !i.progress?.completed);
      setActiveId((firstIncomplete || res.items?.[0])?.item_id || null);
    }).catch(() => toast.error('Failed to load assignment'));
  }, [load]);

  // Time heartbeat: 30s of learning time attributed to the active item
  useEffect(() => {
    const t = setInterval(() => {
      if (activeRef.current) {
        post(`/me/learning/${assignmentId}/items/${activeRef.current}/progress`,
          { time_delta_seconds: 30 }).catch(() => {});
      }
    }, 30000);
    return () => clearInterval(t);
  }, [assignmentId, post]);

  const reportProgress = useCallback(async (itemId, payload) => {
    try {
      await post(`/me/learning/${assignmentId}/items/${itemId}/progress`, payload);
      // Refresh rollup quietly (viewer keeps its own local state)
      const res = await get(`/me/learning/${assignmentId}`);
      setData(res);
    } catch { /* non-fatal */ }
  }, [assignmentId, post, get]);

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const items = data.items || [];
  const active = items.find(i => i.item_id === activeId);
  const allItemsDone = items.every(i => i.progress?.completed);

  return (
    <div className="space-y-4" data-testid="assignment-player-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button asChild variant="ghost" size="sm" data-testid="back-to-learning-btn">
          <Link to="/my-learning"><ArrowLeft className="w-4 h-4 mr-1" /> My Learning</Link>
        </Button>
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold truncate">{data.title}</h1>
          <p className="text-xs text-muted-foreground">
            Assigned by {data.assigned_by || '—'}
            {data.due_date && <> · Due {new Date(data.due_date).toLocaleDateString()}</>}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 w-48">
          <Progress value={data.progress_pct} className="h-2" />
          <span className="text-xs font-medium">{Math.round(data.progress_pct)}%</span>
        </div>
      </div>
      {data.description && <p className="text-sm text-muted-foreground">{data.description}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 space-y-2">
          <Card className="divide-y" data-testid="item-rail">
            {items.map(it => {
              const Icon = TYPE_ICON[it.type] || FileText;
              const isActive = it.item_id === activeId && !showQuiz;
              return (
                <button key={it.item_id}
                        onClick={() => { setActiveId(it.item_id); setShowQuiz(false); }}
                        className={`w-full text-left p-3 flex items-center gap-3 hover:bg-muted/50 transition ${isActive ? 'bg-muted' : ''}`}
                        data-testid={`item-rail-${it.item_id}`}>
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{it.title}</p>
                    <p className="text-xs text-muted-foreground">{Math.round(it.progress?.pct || 0)}%</p>
                  </div>
                  {it.progress?.completed
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    : <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0" />}
                </button>
              );
            })}
            {data.has_quiz && (
              <button onClick={() => setShowQuiz(true)}
                      disabled={!allItemsDone && !data.quiz_attempt}
                      className={`w-full text-left p-3 flex items-center gap-3 hover:bg-muted/50 transition disabled:opacity-50 ${showQuiz ? 'bg-muted' : ''}`}
                      data-testid="quiz-rail-item">
                <Award className="w-4 h-4 text-amber-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Quiz</p>
                  <p className="text-xs text-muted-foreground">
                    {data.quiz_attempt
                      ? `Scored ${data.quiz_attempt.score_pct}%`
                      : allItemsDone ? 'Ready to take' : 'Finish all items first'}
                  </p>
                </div>
                {data.quiz_attempt && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
            )}
          </Card>
          {data.status === 'completed' && (
            <Card className="p-3 bg-emerald-50 border-emerald-200 text-emerald-800 text-sm flex items-center gap-2"
                  data-testid="assignment-complete-banner">
              <CheckCircle2 className="w-4 h-4" /> Assignment completed. Great work!
            </Card>
          )}
        </div>

        <div className="lg:col-span-8">
          {showQuiz && data.has_quiz ? (
            <QuizSection assignmentId={assignmentId} attempt={data.quiz_attempt} onSubmitted={load} />
          ) : active ? (
            <ItemViewer item={active} assignmentId={assignmentId} onProgress={(p) => reportProgress(active.item_id, p)} />
          ) : (
            <Card className="p-10 text-center text-sm text-muted-foreground">Select an item to start.</Card>
          )}
          <QASection assignmentId={assignmentId} />
        </div>
      </div>
    </div>
  );
}

function ItemViewer({ item, assignmentId, onProgress }) {
  const cb = useCallback((p) => onProgress(p), [onProgress, item.item_id]); // eslint-disable-line react-hooks/exhaustive-deps
  switch (item.type) {
    case 'module': return <ModuleViewer key={item.item_id} item={item} onProgress={cb} />;
    case 'document':
      return item.meta?.is_pdf
        ? <PdfViewer key={item.item_id} item={item} onProgress={cb} />
        : <LinkViewer key={item.item_id} item={item} onProgress={cb} />;
    case 'video': return <VideoViewer key={item.item_id} item={item} onProgress={cb} />;
    case 'youtube': return <YouTubeViewer key={item.item_id} item={item} onProgress={cb} />;
    case 'scorm': return <ScormViewer key={item.item_id} item={item} assignmentId={assignmentId} onProgress={cb} />;
    default: return <LinkViewer key={item.item_id} item={item} onProgress={cb} />;
  }
}

function QuizSection({ assignmentId, attempt, onSubmitted }) {
  const { get, post } = useApi();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({}); // qid -> {selected: Set, text}
  const [result, setResult] = useState(attempt || null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (result) return;
    get(`/me/learning/${assignmentId}/quiz`)
      .then(setQuiz)
      .catch(() => toast.error('Failed to load quiz'));
  }, [get, assignmentId, result]);

  const toggleOption = (qid, oid) => {
    setAnswers(a => {
      const cur = new Set(a[qid]?.selected || []);
      if (cur.has(oid)) cur.delete(oid); else cur.add(oid);
      return { ...a, [qid]: { ...a[qid], selected: cur } };
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: (quiz?.questions || []).map(q => ({
          question_id: q.question_id,
          selected_option_ids: [...(answers[q.question_id]?.selected || [])],
          text_answer: answers[q.question_id]?.text || null,
        })),
      };
      const res = await post(`/me/learning/${assignmentId}/quiz/submit`, payload);
      setResult(res);
      toast.success(`Quiz submitted — ${res.score_pct}%`);
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Card className="p-8 text-center" data-testid="quiz-result">
        <Award className="w-10 h-10 mx-auto text-amber-500 mb-3" />
        <p className="font-display text-2xl font-bold">{result.score_pct}%</p>
        <p className="text-sm text-muted-foreground mt-1">
          {result.correct} of {result.total} correct · Quiz submitted
        </p>
      </Card>
    );
  }

  if (!quiz) return <p className="text-sm text-muted-foreground p-6">Loading quiz…</p>;

  return (
    <div className="space-y-4" data-testid="quiz-section">
      <h3 className="font-display text-lg font-semibold">{quiz.quiz?.title || 'Quiz'}</h3>
      {(quiz.questions || []).map((q, qi) => (
        <Card key={q.question_id} className="p-4" data-testid={`quiz-question-${q.question_id}`}>
          <p className="font-medium text-sm mb-3">{qi + 1}. {q.question_text}</p>
          {q.options?.length > 0 ? (
            <div className="space-y-2">
              {q.options.map(o => (
                <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={answers[q.question_id]?.selected?.has(o.id) || false}
                    onCheckedChange={() => toggleOption(q.question_id, o.id)}
                    data-testid={`quiz-option-${q.question_id}-${o.id}`}
                  />
                  {o.text}
                </label>
              ))}
            </div>
          ) : (
            <Input placeholder="Your answer"
                   value={answers[q.question_id]?.text || ''}
                   onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { ...a[q.question_id], text: e.target.value } }))}
                   data-testid={`quiz-text-${q.question_id}`} />
          )}
        </Card>
      ))}
      <Button onClick={submit} disabled={submitting} data-testid="quiz-submit-btn">
        {submitting ? 'Submitting…' : 'Submit Quiz'}
      </Button>
      <p className="text-xs text-muted-foreground">One attempt only — review your answers before submitting.</p>
    </div>
  );
}
